import React, { useState, useRef, useEffect } from "react";
import { X, Calendar, FileText, Award, Layers, Loader2, ChevronDown, Check, Trash2 } from "lucide-react";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal";

const CreateAssignmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialData = null,
  categories = [],
  loading = false,
}) => {
  const isEditing = Boolean(initialData?.id);

  const defaultFormState = {
    title: "",
    description: "",
    category: categories.length > 0 ? String(categories[0].id) : "",
    maxPoints: 100,
    dueDate: "",
  };

  const [formData, setFormData] = useState(defaultFormState);
  const [errors, setErrors] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for delete modal
  const dropdownRef = useRef(null);

  // Sync form state when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          title: initialData.title || "",
          description: initialData.description || "",
          category: initialData.category_id ? String(initialData.category_id) : (categories[0]?.id ? String(categories[0].id) : ""),
          maxPoints: initialData.maxPoints ?? 100,
          dueDate: initialData.dueDate || "",
        });
      } else {
        setFormData({
          ...defaultFormState,
          category: categories.length > 0 ? String(categories[0].id) : "",
        });
      }
      setErrors({});
      setIsDeleteModalOpen(false);
    } else {
      setIsDropdownOpen(false);
      setIsDeleteModalOpen(false);
    }
  }, [isOpen, initialData, categories]);

  // Outside click & Escape key listeners
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen && !isDeleteModalOpen) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isDeleteModalOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectCategory = (catId) => {
    setFormData((prev) => ({ ...prev, category: String(catId) }));
    if (errors.category) {
      setErrors((prev) => ({ ...prev, category: "" }));
    }
    setIsDropdownOpen(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Assignment title is required.";
    if (!formData.category) newErrors.category = "Please select a category.";
    if (!formData.maxPoints || formData.maxPoints <= 0) {
      newErrors.maxPoints = "Max points must be greater than 0.";
    }
    if (!formData.dueDate) newErrors.dueDate = "Due date is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(formData);
  };

  // Open the custom delete modal instead of browser confirm
  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  // Confirm execution handler passed to DeleteConfirmationModal
  const handleConfirmDelete = () => {
    if (isEditing && onDelete) {
      onDelete(initialData.id);
    }
    setIsDeleteModalOpen(false);
  };

  const selectedCategoryObj = categories.find(
    (cat) => String(cat.id) === String(formData.category)
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
        <div className="bg-white border border-zinc-200 rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-zinc-50/50">
            <div>
              <h3 className="text-sm font-bold text-zinc-900">
                {isEditing ? "Edit Assignment" : "Create New Assignment"}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                {isEditing ? "Update column details in your gradebook" : "Add a new column to your gradebook"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="p-1 text-zinc-400 hover:text-zinc-600 rounded-lg hover:bg-zinc-100 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            
            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                Title <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <FileText className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Midterm Exam, Quiz 1"
                  className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-xs font-medium focus:outline-none transition-all ${
                    errors.title ? "border-rose-300 focus:ring-1 focus:ring-rose-500" : "border-zinc-200 focus:ring-1 focus:ring-blue-500"
                  }`}
                />
              </div>
              {errors.title && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.title}</p>}
            </div>

            {/* Category & Max Points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div ref={dropdownRef} className="relative">
                <label className="block text-xs font-semibold text-zinc-700 mb-1">
                  Category <span className="text-rose-500">*</span>
                </label>
                
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen((prev) => !prev)}
                  className={`w-full relative flex items-center justify-between pl-9 pr-3 py-2 bg-white border rounded-lg text-xs font-medium text-left transition-all min-h-[38px] ${
                    errors.category
                      ? "border-rose-300 focus:ring-1 focus:ring-rose-500"
                      : "border-zinc-200 focus:ring-1 focus:ring-blue-500"
                  }`}
                >
                  <Layers className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                  
                  <div className="flex items-center gap-2 overflow-hidden">
                    {selectedCategoryObj && (
                      <span
                        className="w-3 h-3 rounded-full shrink-0 border border-black/10"
                        style={{ backgroundColor: selectedCategoryObj.color || "#3B82F6" }}
                      />
                    )}
                    <span className={`truncate ${selectedCategoryObj ? "text-zinc-900 font-semibold" : "text-zinc-400"}`}>
                      {selectedCategoryObj
                        ? `${selectedCategoryObj.name} (${selectedCategoryObj.weight}%)`
                        : "Select category..."}
                    </span>
                  </div>

                  <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {isDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1 z-10 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
                    {categories.length > 0 ? (
                      categories.map((cat) => {
                        const isSelected = String(cat.id) === String(formData.category);
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleSelectCategory(cat.id)}
                            className={`w-full text-left px-3 py-2 text-xs font-medium flex items-center justify-between transition-colors ${
                              isSelected ? "bg-zinc-50 font-semibold text-zinc-900" : "text-zinc-700 hover:bg-zinc-50"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full shrink-0 border border-black/10"
                                style={{ backgroundColor: cat.color || "#3B82F6" }}
                              />
                              <span>{cat.name} ({cat.weight}%)</span>
                            </div>
                            {isSelected && <Check className="w-3.5 h-3.5 text-zinc-800" />}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-xs text-zinc-400 italic">No categories available</div>
                    )}
                  </div>
                )}

                {errors.category && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.category}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">
                  Max Points <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Award className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="number"
                    name="maxPoints"
                    value={formData.maxPoints}
                    onChange={handleChange}
                    min="1"
                    className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-xs font-medium focus:outline-none transition-all ${
                      errors.maxPoints ? "border-rose-300 focus:ring-1 focus:ring-rose-500" : "border-zinc-200 focus:ring-1 focus:ring-blue-500"
                    }`}
                  />
                </div>
                {errors.maxPoints && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.maxPoints}</p>}
              </div>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                Due Date <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-xs font-medium focus:outline-none transition-all ${
                    errors.dueDate ? "border-rose-300 focus:ring-1 focus:ring-rose-500" : "border-zinc-200 focus:ring-1 focus:ring-blue-500"
                  }`}
                />
              </div>
              {errors.dueDate && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.dueDate}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                Description <span className="text-zinc-400 font-normal">(Optional)</span>
              </label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                placeholder="Provide assignment guidelines or details..."
                className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all resize-none placeholder-zinc-400"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
              {isEditing ? (
                <button
                  type="button"
                  onClick={handleOpenDeleteModal}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-xs font-semibold hover:bg-rose-100 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              ) : <div />}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-xs cursor-pointer disabled:opacity-50"
                >
                  {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {loading ? "Saving..." : isEditing ? "Save Changes" : "Create Assignment"}
                </button>
              </div>
            </div>
          </form>

        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Assignment"
        message={`Are you sure you want to delete "${formData.title || "this assignment"}"? This action cannot be undone.`}
        loading={loading}
      />
    </>
  );
};

export default CreateAssignmentModal;